/**
 * The remaining administration screens — master data, workflows, audit and
 * security. `docs/html/master-data.html` and `docs/html/admin.html`
 * (`#master`, `#workflow`, `#audit`, `#security`) ported.
 *
 * These read from fixtures until their API modules exist; the Users & Roles
 * screens next door are live.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  listWorkflows,
  listAuditTrail, getSecurityPolicy, listScoringRules,
} from '../../api/crm.api.js';
import { queryKeys } from '../../app/queryClient.js';
import { isMock } from '../../api/mockFallback.js';
import { useCursorPages } from '../../hooks/useCursorPages.js';
import { useDebounce } from '../../hooks/useDebounce.js';
import { useAuth } from '../../hooks/useAuth.js';
import { DataTable } from '../../components/common/DataTable.jsx';
import { PageLoader, PageHeader, Card, Badge, Avatar, SearchInput, ChipSelect, MockBadge, Spinner, ErrorState, EmptyState, Meter } from '../../components/common/index.jsx';
import { dateTime, number } from '../../utils/formatters.js';

/* -------------------------------------------------------------------------- */
/* Workflows                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Automation rules — `docs/html/admin.html#workflow`.
 *
 * Each rule is shown as trigger → condition → actions, in that order, because
 * that is the sentence the rule actually is. A table of names and statuses would
 * make an admin open every one to find out what it does.
 */
export function WorkflowsPage() {
  const { can } = useAuth();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.admin.workflows,
    queryFn: () => listWorkflows(),
  });
  const scoring = useQuery({ queryKey: queryKeys.admin.scoringRules, queryFn: listScoringRules });

  return (
    <>
      <PageHeader
        title="Automation"
        subtitle="Rules that act without anyone clicking."
        badge={isMock(data) ? <MockBadge section="workflows" /> : null}
      >
        {can('admin.workflow', 'create') && (
          <button type="button" className="btn btn-add btn-sm-x">
            <i className="bi bi-plus-lg me-1" aria-hidden="true" />New rule
          </button>
        )}
      </PageHeader>

      <Card title="Workflow rules" className="mb-3">
        {isLoading ? <Spinner /> : error ? <ErrorState error={error} onRetry={refetch} /> : (
          <ul className="list-x">
            {(data?.data ?? []).map((wf) => (
              <li key={wf.id} className="list-x-item align-items-start">
                <div className="flex-grow-1">
                  <div className="fw-600 t-sm">{wf.name}</div>
                  <div className="t-xs text-muted">
                    <strong>When</strong> {wf.trigger}
                    {wf.condition && <> · <strong>if</strong> {wf.condition}</>}
                    {' · '}<strong>then</strong> {wf.actions} action{wf.actions === 1 ? '' : 's'}
                  </div>
                  <div className="t-2xs text-muted mt-1">
                    {number(wf.runs)} runs · last {wf.lastRun}
                  </div>
                </div>
                <Badge tone={wf.status === 'Active' ? 'emerald' : 'amber'}>{wf.status}</Badge>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="Lead scoring rules" subtitle="What contributes to a lead's score, and by how much">
        {scoring.isLoading ? <Spinner /> : (
          <div className="table-x-wrap">
            <table className="table-x is-dense">
              <thead>
                <tr>
                  <th>Signal</th>
                  <th>Field</th>
                  <th className="text-end">Points each</th>
                  <th className="text-end">Cap</th>
                  <th style={{ width: 100 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {(scoring.data?.data ?? []).map((rule) => (
                  <tr key={rule.id}>
                    <td className="fw-600">{rule.label}</td>
                    <td><code className="t-xs">{rule.field}</code></td>
                    <td className="text-end">{rule.points}</td>
                    <td className="text-end">{rule.cap}</td>
                    <td><Badge tone={rule.enabled ? 'emerald' : 'neutral'}>{rule.enabled ? 'On' : 'Off'}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="t-2xs text-muted mb-0 mt-2">
          Scores are recomputed on a schedule, so a change here takes effect on the next run rather
          than immediately.
        </p>
      </Card>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Audit                                                                       */
/* -------------------------------------------------------------------------- */

const AUDIT_ACTIONS = ['create', 'update', 'delete', 'login_failed', 'permission_change', 'export', 'approve', 'stage_change', 'send'];
const AUDIT_RESULTS = ['success', 'failure'];

/**
 * The audit trail — `docs/html/admin.html#audit`.
 *
 * Read-only, and deliberately offers no edit or delete control anywhere. The
 * underlying tables are append-only and hash-chained; a UI affordance that
 * implied otherwise would be lying about what the system can do.
 */
export function AuditPage() {
  const [search, setSearch] = useState('');
  const [action, setAction] = useState('');
  const [result, setResult] = useState('');

  const debounced = useDebounce(search);
  const pages = useCursorPages(25);

  const params = useMemo(
    () => ({ ...pages.params, q: debounced || undefined, action: action || undefined, result: result || undefined }),
    [pages.params, debounced, action, result],
  );

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.admin.audit(params),
    queryFn: () => listAuditTrail(params),
    placeholderData: (previous) => previous,
  });

  const onFilter = (setter) => (value) => { setter(value); pages.reset(); };

  const columns = [
    { key: 'at', header: 'When', width: 170, render: (r) => <span className="t-xs">{dateTime(r.at)}</span> },
    {
      key: 'actor',
      header: 'Actor',
      width: 170,
      render: (r) => (
        <div className="d-flex align-items-center gap-2">
          <Avatar name={r.actor} size={24} />
          <span className="t-sm">{r.actor}</span>
        </div>
      ),
    },
    { key: 'action', header: 'Action', width: 150, render: (r) => <code className="t-xs">{r.action}</code> },
    { key: 'record', header: 'Record', width: 150, render: (r) => <span className="t-xs">{r.entity} · {r.record}</span> },
    { key: 'detail', header: 'Detail' },
    { key: 'ip', header: 'IP', width: 120, render: (r) => <span className="t-xs text-muted">{r.ip}</span> },
    {
      key: 'result',
      header: 'Result',
      width: 90,
      render: (r) => <Badge tone={r.result === 'success' ? 'emerald' : 'rose'}>{r.result}</Badge>,
    },
  ];

  return (
    <>
      <PageHeader
        title="Audit log"
        subtitle="Every change, who made it and from where."
        badge={isMock(data) ? <MockBadge section="audit" /> : null}
      />

      <div className="alert-x alert-info-x mb-3">
        <i className="bi bi-shield-lock me-2" aria-hidden="true" />
        These rows are append-only and hash-chained in the database. They cannot be edited or
        deleted from this application, by anyone, including an administrator.
      </div>

      <Card bodyClass="p-0">
        <div className="filter-bar">
          <SearchInput value={search} onChange={onFilter(setSearch)} placeholder="Search actor, record or detail…" className="flex-grow-1" />
          <ChipSelect label="Action" allLabel="All actions" value={action} onChange={onFilter(setAction)} options={AUDIT_ACTIONS} />
          <ChipSelect label="Result" allLabel="All results" value={result} onChange={onFilter(setResult)} options={AUDIT_RESULTS} />
        </div>

        <DataTable
          columns={columns}
          rows={data?.data ?? []}
          isLoading={isLoading}
          error={error}
          onRetry={refetch}
          page={data?.page}
          onNext={pages.goNext}
          onPrevious={pages.goPrevious}
          canGoPrevious={pages.canGoPrevious}
          rowKey={(r) => String(r.id)}
          dense
          emptyTitle="No audit entries match these filters"
        />
      </Card>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Security                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The organisation's security policy — `docs/html/admin.html#security`.
 *
 * Grouped by what each setting protects against rather than by database column,
 * so an administrator reading it can tell what tightening a number would buy.
 */
export function SecurityPage() {
  const { can } = useAuth();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.admin.securityPolicy,
    queryFn: getSecurityPolicy,
  });

  if (isLoading) return <PageLoader label="Loading security policy" />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  const groups = [
    {
      title: 'Password strength',
      note: 'Checked on every password set or change, and against the reuse history.',
      rows: [
        ['Minimum length', `${data.password_min_length} characters`],
        ['Upper and lower case', data.password_require_mixed ? 'Required' : 'Not required'],
        ['A digit', data.password_require_digit ? 'Required' : 'Not required'],
        ['A symbol', data.password_require_symbol ? 'Required' : 'Not required'],
        ['Reuse history', `Last ${data.password_history_depth} cannot be reused`],
        ['Maximum age', data.password_max_age_days ? `${data.password_max_age_days} days` : 'No expiry'],
      ],
    },
    {
      title: 'Brute-force protection',
      note: 'Counted per account. The lock clears itself once the window passes.',
      rows: [
        ['Failed attempts before lock', number(data.max_failed_attempts)],
        ['Lock duration', `${data.lockout_minutes} minutes`],
      ],
    },
    {
      title: 'Sessions',
      note: 'The idle window slides on activity; the absolute limit does not.',
      rows: [
        ['Idle timeout', `${data.session_idle_minutes} minutes`],
        ['Maximum session length', `${data.session_absolute_hours} hours`],
        ['Single sign-on only', data.sso_only ? 'Yes — password sign-in refused' : 'No'],
      ],
    },
    {
      title: 'Retention',
      note: 'How long the trail is kept before it is aged out.',
      rows: [
        ['Audit trail', `${data.audit_retention_years} years`],
        ['System logs', `${data.log_retention_days} days`],
      ],
    },
  ];

  return (
    <>
      <PageHeader
        title="Security"
        subtitle="Password, lockout, session and retention policy for this organisation."
        badge={isMock(data) ? <MockBadge section="security policy" /> : null}
      >
        {can('admin.security', 'edit') && (
          <button type="button" className="btn btn-edit btn-sm-x">
            <i className="bi bi-pencil me-1" aria-hidden="true" />Edit policy
          </button>
        )}
      </PageHeader>

      <div className="grid-2">
        {groups.map((group) => (
          <Card key={group.title} title={group.title} subtitle={group.note}>
            <dl className="detail-list">
              {group.rows.map(([label, value]) => (
                <div key={label} className="detail-row">
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          </Card>
        ))}
      </div>
    </>
  );
}
